import React from "react";
import styled from "styled-components";
import EmergencyOutlinedIcon from "@mui/icons-material/EmergencyOutlined";
import AirportShuttleOutlinedIcon from "@mui/icons-material/AirportShuttleOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

interface DashboardHeaderProps {
  ambulanceCount: number;
  ambulancesWithLocation: number;
  nextAmbulanceETA: string | null;
  nextAmbulanceArrivalTime: string | null;
  loading: boolean;
  audioEnabled: boolean;
  onToggleAudio: () => void;
}

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 0;
  margin-bottom: 20px;
`;

const StatCards = styled.div`
  display: flex;
  gap: 20px;
  flex: 1;
`;

const StatCard = styled.div`
  background-color: var(--background-secondary);
  border-radius: 8px;
  padding: 16px;
  min-width: 200px;
  flex: 1;
  display: flex;
  align-items: center;
  box-shadow: var(--box-shadow);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const IconContainer = styled.div<{ $color?: string }>`
  width: 50px;
  height: 50px;
  border-radius: 8px;
  background-color: ${props => props.$color ? `rgba(220, 0, 0, 0.1)` : 'var(--primary-light)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  color: ${props => props.$color || 'var(--primary)'};
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatTitle = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 4px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: var(--text-primary);
`;

const AudioControls = styled.div`
  display: flex;
  align-items: center;
`;

const AudioButton = styled.button<{ enabled: boolean }>`
  background-color: ${(props) =>
    props.enabled ? "var(--primary)" : "var(--background-secondary)"};
  color: ${(props) => (props.enabled ? "white" : "var(--text-secondary)")};
  border: 1px solid
    ${(props) => (props.enabled ? "var(--primary)" : "var(--border-color)")};
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      props.enabled ? "var(--primary-dark)" : "var(--background-hover)"};
  }
`;

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  ambulanceCount,
  ambulancesWithLocation,
  nextAmbulanceETA,
  nextAmbulanceArrivalTime,
  loading,
  audioEnabled,
  onToggleAudio,
}) => {
  return (
    <HeaderContainer>
      <StatCards>
        <StatCard>
          <IconContainer $color="#d32f2f">
            <EmergencyOutlinedIcon />
          </IconContainer>
          <StatContent>
            <StatTitle>Ambulances En Route to Huntington Hospital</StatTitle>
            <StatValue>{loading ? "..." : ambulanceCount}</StatValue>
          </StatContent>
        </StatCard>

        <StatCard>
          <IconContainer>
            <AirportShuttleOutlinedIcon />
          </IconContainer>
          <StatContent>
            <StatTitle>With Location Data</StatTitle>
            <StatValue>
              {loading ? "..." : `${ambulancesWithLocation}/${ambulanceCount}`}
            </StatValue>
          </StatContent>
        </StatCard>

        <StatCard>
          <IconContainer>
            <AccessTimeOutlinedIcon />
          </IconContainer>
          <StatContent>
            <StatTitle>Next Arrival</StatTitle>
            <StatValue>
              {loading ? "..." : nextAmbulanceETA || "None"}
              {!loading && nextAmbulanceArrivalTime && (
                <span style={{ display: "block", fontSize: "0.8em" }}>
                  {nextAmbulanceArrivalTime}
                </span>
              )}
            </StatValue>
          </StatContent>
        </StatCard>
      </StatCards>

      {/* <AudioControls>
        <AudioButton enabled={audioEnabled} onClick={onToggleAudio}>
          {audioEnabled ? "Notifications On" : "Notifications Off"}
        </AudioButton>
      </AudioControls> */}
    </HeaderContainer>
  );
};

export default DashboardHeader;
